import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getRequestData } from 'api/api';
import { endpoints } from 'api/endpoints';
import {
  IconFilter,
  IconReset,
  IconSearch,
  IconSmallArrow,
  IconWallet,
  InvoiceHistory,
  OrangeButton,
  Pagination,
  SelectOption,
  SmallButton,
  SuccessFailRate,
} from 'components';
import classNames from 'classnames';
import { Input } from 'antd';
import { COLORS } from 'utils/colors';
import { SingleClientRow } from './singleClientRow';
import { CLIENT_STATUS, ROLE_TYPES, SLH_TYPES } from 'api/constants';
import { useAuth } from 'context/authContext';
// eslint-disable-next-line no-unused-vars
import { formatPriceWithComma } from 'utils/helpers';
import { useUser } from 'context/userContext';
import SlhSelect from 'components/slhSelect';

export const FinancesClients = () => {
  const [clients, setClients] = useState();
  const { partnerId, parentId, isParentManaging } = useAuth();
  const { userDetails } = useUser();
  const [partnersList, setPartnersList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(
    +partnerId === +parentId ? null : +partnerId,
  );
  const [searchWord, setSearchWord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [obligations, setObligations] = useState(false);

  const fetchClients = useCallback(async (params) => {
    const { data } = await getRequestData(
      endpoints.getFinancesClients(params),
      ROLE_TYPES.slh_manager,
    );
    if (data) setClients(data);
  }, []);

  const fetchClientObligations = useCallback(async (params) => {
    const { data } = await getRequestData(
      endpoints.getSLhObligations(params),
      ROLE_TYPES.slh_manager,
    );
    if (data) setObligations(data);
  }, []);

  useEffect(() => {
    fetchClients({ page: 1 });
  }, [fetchClients]);

  useEffect(() => {
    fetchClientObligations({ partner_ids: partnerId });
  }, [fetchClientObligations, partnerId]);

  useEffect(() => {
    (async () => {
      const { data } = await getRequestData(endpoints.getAllPartners(), ROLE_TYPES.slh_manager);

      if (data) {
        setPartnersList(data);
      }
    })();
  }, []);

  const storeClients = useCallback(() => {
    return clients.results?.map((client) => (
      <SingleClientRow client={client} key={client.client_id} domain={ROLE_TYPES.slh_manager} />
    ));
  }, [clients?.results]);

  const fetchClientsWithFilters = useCallback(
    (status, partner_id, searchQuery, page = 1) => {
      const filters = {};
      if (status) filters.status = status;
      if (partner_id) filters.partner_ids = partner_id;
      if (searchQuery) filters.search = searchQuery;
      if (page) filters.page = page;
      fetchClients(filters);
    },
    [fetchClients],
  );

  const handleStatusSelectChange = (status) => {
    setSelectedStatus(status);
    fetchClientsWithFilters(status, selectedPartner, searchWord);
  };

  const handleSLHSelectChange = useCallback(
    (id) => {
      setSelectedPartner(id);
      fetchClientsWithFilters(selectedStatus, id, searchWord);
      fetchClientObligations({ partner_ids: id });
    },
    [fetchClientObligations, fetchClientsWithFilters, searchWord, selectedStatus],
  );

  const handleFiltersReset = useCallback(() => {
    setSelectedStatus(null);
    setSearchWord(null);
    setSelectedPartner(null);
    fetchClients({ page: 1, pageSize: 10 });
  }, [fetchClients]);

  const storeBtn = useMemo(() => {
    if (selectedStatus || (selectedPartner && selectedPartner !== +partnerId) || searchWord) {
      return <SmallButton onClick={handleFiltersReset} label="Reset All" icon={<IconReset />} />;
    }
    return null;
  }, [handleFiltersReset, partnerId, searchWord, selectedPartner, selectedStatus]);

  const handleSearch = (e) => {
    const searchTerm = e.target.value;

    if (searchTerm.length > 2 || (searchWord && searchWord.length > searchTerm.length)) {
      fetchClientsWithFilters(selectedStatus, selectedPartner, searchTerm);
    }
    setSearchWord(searchTerm);
  };

  useEffect(() => {
    if (+partnerId === +parentId) {
      return handleSLHSelectChange(null);
    }

    handleSLHSelectChange(partnerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const handleViewInvoice = async () => {
    setIsModalVisible(true);
  };

  return (
    clients && (
      <div className="flex flex-col base-block pt-[30px] mt-6">
        <div
          className={classNames(
            'flex justify-between px-[30px] pb-6 relative border-b border-color-lightBlue',
            'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-lightBlue before:bg-white before:z-10 before:top-full before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
          )}
        >
          <div className="flex items-center gap-x-3">
            <p className="nunito-title">SLH</p>
            {(isParentManaging || userDetails?.partner_details?.type === SLH_TYPES.parent) && (
              <SlhSelect
                data={partnersList}
                value={selectedPartner}
                onChange={(id) => handleSLHSelectChange(id)}
                isWithAll
              />
            )}
          </div>
          <OrangeButton onClick={handleViewInvoice} label="View Invoice" className="self-end" />
          {isModalVisible && (
            <InvoiceHistory
              isOpen={isModalVisible}
              onClose={() => setIsModalVisible(false)}
              partnerId={partnerId}
              selectedPartner={selectedPartner}
              role={ROLE_TYPES.slh_manager}
              partnerName={partnersList.find((p) => p.value === selectedPartner)?.label}
              partnerAddress={partnersList.find((p) => p.value === selectedPartner)?.address}
            />
          )}
        </div>
        <div className="flex border-t border-divider">
          <div className="flex items-start justify-center flex-1 pb-[30px] pt-4">
            <div className="bg-lightGreen rounded-full w-[84px] h-[84px] flex items-center justify-center">
              <IconWallet className="w-10 h-10" color={COLORS.green} />
            </div>
            <div className="flex flex-col ml-5">
              <p className="text-14 text-lightBlue">Active Residents</p>
              <p className="text-24 text-black my-1">{obligations?.active_clients?.count}</p>
              <SuccessFailRate rate={obligations?.active_clients?.trend} />
            </div>
          </div>
          <div
            className={classNames('flex items-start justify-center flex-1 pb-[30px] pt-4 relative')}
          >
            <div className="bg-lightGreen rounded-full w-[84px] h-[84px] flex items-center justify-center">
              <IconWallet className="w-10 h-10" color={COLORS.green} />
            </div>
            <div className="flex flex-col ml-5">
              <p className="text-14 text-lightBlue">Month&apos;s Obligation</p>
              <p className="text-24 text-black my-1">
                ${formatPriceWithComma(obligations?.month?.current_obligation)}
              </p>
              <SuccessFailRate rate={obligations?.month?.month_over_month_percentage} />
            </div>
          </div>
          <div className="flex items-start justify-center flex-1 pb-[30px] pt-4">
            <div className="bg-lightGreen rounded-full w-[84px] h-[84px] flex items-center justify-center">
              <IconWallet className="w-10 h-10" color={COLORS.green} />
            </div>
            <div className="flex flex-col ml-5">
              <p className="text-14 text-lightBlue">Week&apos;s Obligation</p>
              <p className="text-24 text-black my-1">
                ${formatPriceWithComma(obligations?.week?.current_obligation)}
              </p>
              <SuccessFailRate rate={obligations?.week?.week_over_week_percentage} />
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'flex justify-between px-[30px] pb-6 relative border-b border-color-lightBlue',
            'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-lightBlue before:bg-white before:z-10 before:top-full before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
          )}
        ></div>
        <div className="flex items-center gap-x-3 px-[30px] mt-6">
          <p className="nunito-title">{'Residents'}</p>
          <div className="flex items-center gap-x-1 mr-3 ml-auto">
            <IconFilter />
            <p className="text-16">Filter</p>
          </div>
          <SelectOption
            onSelect={(value) => handleStatusSelectChange(value)}
            dropdownStyle={{ padding: '16px', background: 'white' }}
            required
            value={selectedStatus}
            suffixIcon={<IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />}
            className="h-[40px] text-base font-medium base-block rounded-lg"
            popupClassName="min-w-[220px]"
            placeholder="Status"
            dropdownRender={(originalNode) => originalNode}
            optionRender={(option) => (
              <div
                className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${selectedStatus === option.value ? 'border-orange' : ''}`}
              >
                <span
                  className={`w-6 h-6  rounded-full border border-orange ${selectedStatus === option.value ? 'border-[6px]' : ''}`}
                />
                <p className="font-14 flex-1">{option.label}</p>
              </div>
            )}
            options={[
              {
                label: <p className="text-14 px-4 py-1.5 rounded-md">In Progress</p>,
                value: CLIENT_STATUS.in_progress,
              },
              {
                label: <p className="text-14 px-4 py-1.5 rounded-md">Completed</p>,
                value: CLIENT_STATUS.completed,
              },
              {
                label: <p className="text-14 px-4 py-1.5 rounded-md">Ended</p>,
                value: CLIENT_STATUS.failed,
              },
              {
                label: <p className="text-14 px-4 py-1.5 rounded-md">Not Started</p>,
                value: CLIENT_STATUS.not_started,
              },
            ]}
          />
          <Input
            className="search w-[300px] text-lightBlue text-16 rounded-ten bg-light"
            type="text"
            value={searchWord}
            placeholder="Search"
            addonBefore={<IconSearch color={COLORS.lightBlue} />}
            onChange={handleSearch}
          />
          {storeBtn}
        </div>
        <div className="flex flex-col p-[30px] gap-y-3">
          <div className="flex items-center text-lightBlue">
            <p className="text-14 w-[25%]">{'Resident'}</p>
            <p className="text-14 w-[28%]">Home</p>
            <p className="text-14 w-[16.5%]">Status</p>
            <p className="text-14 w-[10%]">Start</p>
            <p className="text-14 w-[9.5%]">Granted</p>
            <p className="text-14 w-[10%]">Remaining</p>
          </div>
          {storeClients()}
          <Pagination
            className="self-center"
            onChange={(page) =>
              fetchClientsWithFilters(selectedStatus, selectedPartner, searchWord, page)
            }
            pageCount={clients.pageCount}
            pageSize={clients.pageSize}
            total={clients.count}
            current={clients.currentPage}
            showSizeChanger={false}
          />
        </div>
      </div>
    )
  );
};
